import { useState } from 'react';
import { Link } from 'react-router';
import { api } from '../api/client.js';
import { Confirm, DataTable, ErrorState, Spinner, StatusBadge } from '../components/ui.jsx';
import { useFetch, useTitle } from '../lib/hooks.js';
import { useMeta } from '../state/MetaContext.jsx';
import { useToast } from '../state/ToastContext.jsx';
import { formatXaf, timeAgo } from '../lib/format.js';
import { OFFER_BADGE } from './landlord/Offers.jsx';

/** The offers somebody has made to buy a house or land, and where each stands. */
const MyOffers = () => {
  useTitle('My offers');
  const toast = useToast();
  const { financeLabel } = useMeta();
  const offers = useFetch('/offers/mine');
  const [withdrawing, setWithdrawing] = useState(null);
  const [busy, setBusy] = useState(false);

  if (offers.loading && !offers.data) return <Spinner />;
  if (offers.error) {
    return (
      <div className="container section">
        <ErrorState message={offers.error} onRetry={offers.reload} />
      </div>
    );
  }

  const withdraw = async () => {
    setBusy(true);
    try {
      await api.post(`/offers/${withdrawing.id}/withdraw`);
      toast.success('Offer withdrawn. The seller has been told.');
      setWithdrawing(null);
      offers.reload();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    {
      key: 'listing',
      header: 'For',
      render: (offer) => (
        <div className="cell-listing">
          <Link to={`/properties/${offer.propertyId}`} className="cell-thumb">
            {offer.property?.coverImage ? <img src={offer.property.coverImage} alt="" /> : <span>No photo</span>}
          </Link>
          <div className="cell-stack">
            <Link to={`/properties/${offer.propertyId}`} className="cell-title">
              {offer.property?.title}
            </Link>
            <span className="muted">
              {offer.property?.city} - asking {formatXaf(offer.property?.price)}
            </span>
          </div>
        </div>
      )
    },
    {
      key: 'offer',
      header: 'Your offer',
      render: (offer) => (
        <div className="cell-stack">
          <strong>{formatXaf(offer.amount)}</strong>
          <span className="muted">{financeLabel(offer.finance)}</span>
          <span className="muted">{timeAgo(offer.createdAt)}</span>
        </div>
      )
    },
    {
      key: 'status',
      header: 'Status',
      render: (offer) => (
        <div className="cell-stack">
          <StatusBadge {...OFFER_BADGE[offer.status]} />
          {offer.decisionNote ? <span className="muted">From the seller: "{offer.decisionNote}"</span> : null}
          {offer.status === 'accepted' ? (
            <Link to={`/offers/${offer.id}/agreement`} className="link-strong">
              Sale agreement
            </Link>
          ) : null}
          {offer.status === 'accepted' && offer.property?.status === 'rented' ? (
            <Link to="/my/land" className="link-strong">
              The seller says it is done - confirm you have it
            </Link>
          ) : null}
        </div>
      )
    },
    {
      key: 'actions',
      header: '',
      render: (offer) =>
        ['submitted', 'accepted'].includes(offer.status) ? (
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setWithdrawing(offer)}>
            Withdraw
          </button>
        ) : null
    }
  ];

  return (
    <div className="container section">
      <header className="page-head">
        <div>
          <h1>My offers</h1>
          <p className="muted">What you have offered for houses and land, and what the sellers said.</p>
        </div>
        <Link to="/search?kind=sale" className="btn btn-primary">
          See what is for sale
        </Link>
      </header>

      <div className="notice notice-info">
        Never pay a deposit before you have seen the land, the land certificate and the site plan. Complete the sale before
        a notary.
      </div>

      <DataTable
        columns={columns}
        rows={offers.data.items}
        empty={{ title: 'No offers yet', message: 'Find a house or a plot for sale and make an offer from its page.' }}
      />

      {withdrawing ? (
        <Confirm
          title="Withdraw this offer?"
          message={`Your offer of ${formatXaf(withdrawing.amount)} for "${withdrawing.property?.title}" is taken back, and the seller is told.`}
          confirmLabel="Withdraw it"
          busy={busy}
          onConfirm={withdraw}
          onClose={() => setWithdrawing(null)}
        />
      ) : null}
    </div>
  );
};

export default MyOffers;
